import { makeVar } from '@apollo/client'

import { ModalComponent, ModalCompositeOption } from '../types'

export interface Modal {
  id: number
  ModalComponent: ModalComponent
  options?: ModalCompositeOption
  isShow: boolean
  once?: boolean
}
export const modalListVar = makeVar<Modal[]>([])
export const latestModalIdVar = makeVar<number>(0)

export const generateModalId = () => {
  const latestModalId = latestModalIdVar()
  const nextModalId = latestModalId + 1
  latestModalIdVar(nextModalId)

  return nextModalId
}

export interface ModalAPI<T extends {}> {
  openModal: (options?: ModalCompositeOption<T> | undefined) => void
  updateModal: (options?: ModalCompositeOption<T> | undefined) => void
  closeModal: () => void
  removeModal: () => void
  modalId: number
}

/**
 * 전역으로 모달을 생성하는 함수이며, 컴포넌트안에서 사용할때는 `useModal` 사용이 권장됨
 * @param ModalComponent 모달에 표시할 컴포넌트
 * @param options `ModalComponent`에 전달될 옵션
 * @param once 모달을 일회성으로 사용할지 여부 (`default:true`), `true`면 모달이 `close`된 이후 3초뒤 자동삭제, 전달하지 않을시 메모리 누수 방지를 위해 직접 삭제 필요
 * @returns 모달 관리에 필요한 함수들 반환
 */
export const createModal = <T extends {}>(
  ModalComponent: ModalComponent<T>,
  options?: ModalCompositeOption<T>,
  once = true,
): ModalAPI<T> => {
  const modalId = generateModalId()

  const newModal: Modal = {
    id: modalId,
    ModalComponent: ModalComponent as ModalComponent,
    options,
    isShow: false,
    once,
  }

  const modalList = modalListVar()

  modalListVar([...modalList, newModal])

  return {
    openModal: (options?: ModalCompositeOption<T>) => showModal(modalId, options),
    updateModal: (options?: ModalCompositeOption<T>) => updateModal(modalId, options),
    closeModal: () => closeModal(modalId),
    removeModal: () => removeModal(modalId),
    modalId,
  }
}

const showModal = (id: number, options?: ModalCompositeOption) => {
  updateModal(id, options, true)
}

export const closeModal = (id: number) => {
  updateModal(id, {}, false)

  const modal = getModal(id)

  // 일회성 모달은 3초뒤에 자동삭제
  if (modal?.once) {
    setTimeout(() => {
      removeModal(modal.id)
    }, 3000)
  }
}

const updateModal = (id: number, options?: ModalCompositeOption, show?: boolean) => {
  const modal = getModal(id)
  if (!modal) return

  const modalList = modalListVar()

  if (modal) {
    if (show !== undefined) modal.isShow = show
    modal.options = { ...modal.options, ...options }
    modalListVar([...modalList])
  }
}

const getModal = (id: number) => {
  const modalList = modalListVar()

  return modalList.find(modal => modal.id === id)
}

export const removeModal = (id: number) => {
  const modalList = modalListVar()

  const newModalList = modalList.filter(modal => modal.id !== id)

  modalListVar([...newModalList])
}

export const closeLatestShowingModal = () => {
  const modalList = modalListVar()
  const showingModalList = modalList.filter(modal => modal.isShow)
  const length = showingModalList.length

  if (!length) return
  const latesShowingtModal = showingModalList[length - 1]
  if (latesShowingtModal.options?.closeOnBackgroundClick === false) return

  closeModal(latesShowingtModal.id)
}
